/**
 * API Client
 *
 * Shared HTTP client with authentication support.
 * Provides axios-like interface using native fetch.
 */

// API base URL - configured via environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

// Token getter function - set by auth system
let getAuthToken: (() => Promise<string | null>) | null = null;

/**
 * Set the authentication token getter function
 * Called by auth system to provide token access
 */
export function setAuthTokenGetter(getter: () => Promise<string | null>): void {
  getAuthToken = getter;
}

/**
 * Response wrapper to match axios-like interface
 */
export interface ApiResponse<T> {
  data: T;
  status: number;
  statusText: string;
}

/**
 * Request options
 */
interface RequestOptions {
  headers?: Record<string, string>;
}

/**
 * Build headers with authentication
 */
async function buildHeaders(
  customHeaders?: Record<string, string>,
): Promise<HeadersInit> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...customHeaders,
  };

  if (getAuthToken) {
    const token = await getAuthToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  return headers;
}

/**
 * Make HTTP request and return response wrapper
 */
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<ApiResponse<T>> {
  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
  const headers = await buildHeaders(options?.headers);

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Handle no-content responses
  if (response.status === 204) {
    return {
      data: undefined as T,
      status: response.status,
      statusText: response.statusText,
    };
  }

  // Parse JSON response
  let data: T;
  try {
    data = await response.json();
  } catch {
    data = undefined as T;
  }

  return {
    data,
    status: response.status,
    statusText: response.statusText,
  };
}

/**
 * API Client with axios-like methods
 */
export const apiClient = {
  /**
   * GET request
   */
  get: <T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> =>
    request<T>("GET", path, undefined, options),

  /**
   * POST request
   */
  post: <T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> => request<T>("POST", path, body, options),

  /**
   * PUT request
   */
  put: <T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> => request<T>("PUT", path, body, options),

  /**
   * DELETE request
   * Note: body parameter added for APIs that expect data in DELETE requests
   */
  delete: <T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> => request<T>("DELETE", path, body, options),

  /**
   * PATCH request
   */
  patch: <T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> => request<T>("PATCH", path, body, options),
};
